import isNil from 'lodash/isNil';
import { parse } from 'date-fns';
import { INVALID_DATE } from '../../ui/constants.tsx';

/** toMilliseconds
 *  this function will normalize time in HH:MM format to milliseconds
 *  e.g. 00:10 = 60000 ms, 01:00 = 3600000 ms
 *  time must be in HH:MM format
 * */
export const toMilliseconds = (time: string) => {
	// time is in hh:mm format. Don't need a date-time utility to convert that to ms
	const [hours, minutes] = time.split(':');
	return (parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60) * 1000;
};

const twoDigitPad = (time: number) => {
	if (time < 10) {
		return `0${time}`;
	}
	return `${time}`;
};

/** toReadableHours
 *  this function will normalize milli seconds to human readable string
 *  result time will be in HH:MM
 * */
export const toReadableHours = (timeInMilliseconds: number) => {
	const hour = Math.floor(timeInMilliseconds / 3600000);
	const minutes = Math.floor((timeInMilliseconds % 3600000) / 60000);
	const hourString = twoDigitPad(hour);
	const minutesString = twoDigitPad(minutes);
	return `${hourString}:${minutesString}`;
};

export const isEmpty = (value?: string) => isNil(value) || value.trim().length < 1;

export const isInvalidFormat = (value: string) => value === INVALID_DATE;

/*
 * This utility converts time in different formats to a Date objects with 24H time
 * Accepted input formats are - HHmm (12H/24H with or without [:.-] separators), with optional am/AM/pm/PM
 * '8' -> 08:00
 * '11' -> 11:00
 * '11p' -> 23:00
 * '12a' -> 00:00
 * '130' -> 13:00
 * '0130' -> 01:30
 * '2:15' -> 02:15
 * '2:15p' -> 14:15
 * '12:15AM' -> 00:15
 * '3.15' -> 03:15
 * '3.15p' -> 15:15
 * '2-30' -> 02:30
 * '2-30p' -> 14:30
 */
export const parseTime = (inputTime: string): Date => {
	// Extract time units from the input string
	const time = inputTime.replace(
		/^\D*(?<hour>\d{1,2})[:.-]?(?<minute>\d{1,2})?\s?(?<period>a|p|A|P)?.*$/,
		(input, ...args) => {
			const { hour, minute = '00', period = '' } = args[args.length - 1];
			return `${hour}:${minute}${period}`;
		},
	);

	// get the parse format based on whether the time has a period (am/pm)
	const format = /^.*[apAP]$/.test(time) ? 'h:mma' : 'H:mm';

	return parse(time, format, new Date());
};
